/**
 * StartCleaningPanel — "Start cleaning" CTA shown on TaskDetailPage
 * when the task is still in `pending` status. Closes the cleaner-
 * lifecycle gap surfaced 2026-05-17: create_cleaning_task makes
 * tasks at status='pending'; submit_cleaning_task requires
 * status='in_progress'. This panel is the missing bridge.
 *
 * Render rules:
 *   - Visible only when task.status === 'pending' (caller-gated).
 *   - Button enabled for:
 *       - the assigned cleaner
 *       - ANY cleaner on an unassigned task (RPC self-claim branch —
 *         start_cleaning_task atomically claims + starts when
 *         p_actor_role='cleaner' and assigned_to_user_id IS NULL)
 *       - any supervisor / contractor_admin / super_admin in the
 *         task's org (only on tasks that already have an assignee —
 *         the RPC has no supervisor self-assign branch, so supervisors
 *         must assign someone first via ReassignTaskPanel)
 *   - Button disabled (with an explanation) when:
 *     - the task has no assignee AND viewer is supervisor+ (must
 *       assign before starting)
 *     - the viewer is a cleaner who is not the assignee on an
 *       already-assigned task
 *
 * Failure surface follows useStartTask's AppError shape — errors render
 * inline below the button (CLAUDE.md ERROR 4: no toast, no swallow).
 */

import { useTranslation }                    from 'react-i18next'
import { type ReactNode }                    from 'react'
import type { TFunction }                    from 'i18next'
import { useStartTask }                      from '@/hooks/useStartTask'
import { isAppError }                        from '@/lib/errors/AppError'
import type { TaskRow }                      from '@/hooks/useTask'
import type { Role }                         from '@/types/roles'

interface Props {
  task:   TaskRow
  role:   Role   | null
  userId: string | null
}

export function StartCleaningPanel({ task, role, userId }: Props): ReactNode {
  const { t } = useTranslation()
  const start = useStartTask()

  const isAssignedCleaner =
    role === 'cleaner' && userId !== null && task.assigned_to_user_id === userId
  const isSupervisorOrAbove =
    role === 'supervisor' || role === 'contractor_admin' || role === 'super_admin'
  const hasAssignee = task.assigned_to_user_id !== null

  const blockReason = computeBlockReason({ task, role, userId, isAssignedCleaner, isSupervisorOrAbove, t })
  const disabled    = start.isPending || blockReason !== null

  return (
    <section
      aria-label={t('startTask.panelLabel', 'Start cleaning')}
      style={{
        background:   '#fff7ed',
        border:       '2px solid #ea580c',
        borderRadius: '8px',
        padding:      '0.85rem 1rem',
        display:      'flex',
        flexDirection: 'column',
        gap:          '0.5rem',
      }}
    >
      <p style={{ margin: 0, fontSize: '0.95rem', color: '#111827', fontWeight: 600 }}>
        {hasAssignee
          ? t('startTask.prompt', 'Ready to begin? Tapping start marks the task in progress and moves the room into cleaning.')
          : role === 'cleaner'
            ? t('startTask.unassignedCleaner', 'No cleaner is assigned yet. Tap Start to claim this job and begin cleaning.')
            : t('startTask.unassigned', 'This task has not been assigned to a cleaner yet. Assign someone before starting.')}
      </p>

      <button
        type="button"
        onClick={() => { void start.mutateAsync({ taskId: task.id }) }}
        disabled={disabled}
        style={{
          padding:      '0.75rem 1.1rem',
          background:   disabled ? '#9ca3af' : '#ea580c',
          color:        '#ffffff',
          border:       '2px solid #c2410c',
          borderRadius: '6px',
          fontSize:     '1.05rem',
          fontWeight:   700,
          cursor:       disabled ? 'not-allowed' : 'pointer',
          boxShadow:    '0 2px 6px rgba(234,88,12,0.25)',
        }}
      >
        {start.isPending
          ? t('startTask.starting', 'Starting…')
          : t('startTask.start', 'Start cleaning')}
      </button>

      {blockReason && !start.isPending && (
        <p role="status" style={{ margin: 0, color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
          {blockReason}
        </p>
      )}

      {start.error && isAppError(start.error) && (
        <p
          role="alert"
          style={{
            margin:       0,
            padding:      '0.5rem 0.75rem',
            background:   'var(--brand-danger-subtle)',
            color:        'var(--brand-danger)',
            border:       '1px solid var(--brand-danger)',
            borderRadius: 'var(--brand-radius-md)',
            fontSize:     '0.85rem',
          }}
        >
          {start.error.userMessage}
        </p>
      )}
    </section>
  )
}

interface BlockArgs {
  task:                TaskRow
  role:                Role   | null
  userId:              string | null
  isAssignedCleaner:   boolean
  isSupervisorOrAbove: boolean
  t:                   TFunction
}

function computeBlockReason({ task, role, userId, isAssignedCleaner, isSupervisorOrAbove, t }: BlockArgs): string | null {
  if (task.assigned_to_user_id === null) {
    // Cleaners may self-claim — start_cleaning_task RPC atomically claims
    // the task to the actor when p_actor_role='cleaner' on an unassigned
    // task (commit fe5f79f, "self-claim unassigned tasks").
    if (role === 'cleaner' && userId !== null) return null
    return t('startTask.blockUnassigned', 'A supervisor must assign a cleaner before this task can start.')
  }
  if (isSupervisorOrAbove) return null
  if (isAssignedCleaner)   return null
  if (role === 'cleaner' && userId !== null && task.assigned_to_user_id !== userId) {
    return t('startTask.blockNotMine', 'Only the assigned cleaner can start this task.')
  }
  return t('startTask.blockRole', 'Your role cannot start this task.')
}
